import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TouchButton from './ui/TouchButton';
import { TouchInput, TouchTextArea, TouchSelect, TouchFormGroup } from './ui/TouchForm';

const WIZARD_STEPS = {
  LOCATION: 'location',
  RATING: 'rating', 
  PHOTOS: 'photos',
  COMMENTS: 'comments',
  REVIEW: 'review'
};

const STEP_ORDER = [
  WIZARD_STEPS.LOCATION,
  WIZARD_STEPS.RATING,
  WIZARD_STEPS.PHOTOS,
  WIZARD_STEPS.COMMENTS,
  WIZARD_STEPS.REVIEW
];

const ReviewWizard = ({ onSubmit, onCancel, initialData = {} }) => {
  const { isAuthenticated, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState(() => {
    const savedDraft = localStorage.getItem('review-draft');
    const draftData = savedDraft ? JSON.parse(savedDraft) : {};
    
    return {
      locationName: initialData.locationName || draftData.locationName || '',
      coordinates: initialData.coordinates || draftData.coordinates || null,
      lightingQuality: initialData.lightingQuality || draftData.lightingQuality || 0,
      crowdLevels: initialData.crowdLevels || draftData.crowdLevels || 0,
      harassmentIncident: initialData.harassmentIncident || draftData.harassmentIncident || false,
      harassmentSeverity: initialData.harassmentSeverity || draftData.harassmentSeverity || 'low',
      staffHelpfulness: initialData.staffHelpfulness || draftData.staffHelpfulness || 0,
      overallSafety: initialData.overallSafety || draftData.overallSafety || 0,
      comments: initialData.comments || draftData.comments || '',
      photos: initialData.photos || draftData.photos || [],
      timestamp: initialData.timestamp || draftData.timestamp || new Date().toISOString()
    };
  });

  // Initialize voice recognition
  useEffect(() => {
    if (typeof window !== 'undefined' && window.webkitSpeechRecognition) {
      const speechRecognition = new window.webkitSpeechRecognition();
      speechRecognition.continuous = false;
      speechRecognition.interimResults = false;
      speechRecognition.lang = 'en-US';
      
      speechRecognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setFormData(prev => ({
          ...prev,
          comments: prev.comments + (prev.comments ? ' ' : '') + transcript
        }));
        setIsRecording(false);
      };
      
      speechRecognition.onerror = () => setIsRecording(false);
      speechRecognition.onend = () => setIsRecording(false);
      
      setRecognition(speechRecognition);
    }
  }, []);

  // Save draft to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('review-draft', JSON.stringify(formData));
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser');
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        updateFormData('coordinates', coords);
        
        // Reverse geocode to get location name
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${coords.lat}&lon=${coords.lng}&format=json`)
          .then(res => res.json())
          .then(data => {
            if (data.display_name) {
              updateFormData('locationName', data.display_name);
            }
          })
          .catch(() => {
            updateFormData('locationName', `Location: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`);
          })
          .finally(() => setIsLoadingLocation(false));
      },
      (error) => {
        setIsLoadingLocation(false);
        alert('Unable to get your location: ' + error.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleCameraCapture = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      if (file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPhoto = {
            id: Date.now() + Math.random(),
            file: file,
            preview: e.target.result,
            name: file.name,
            timestamp: new Date().toISOString()
          };
          
          setFormData(prev => ({
            ...prev,
            photos: [...prev.photos, newPhoto]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removePhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(photo => photo.id !== photoId)
    }));
  };

  const startVoiceRecording = () => {
    if (recognition) {
      setIsRecording(true);
      recognition.start();
    } else {
      alert('Voice recording is not supported in this browser');
    }
  };

  const nextStep = () => {
    if (currentStep < STEP_ORDER.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (!isAuthenticated) {
      alert('Please log in to submit a review');
      return;
    }
    
    if (formData.overallSafety === 0) {
      alert('Please provide an overall safety rating');
      return;
    }

    const reviewData = {
      ...formData,
      id: Date.now(),
      userId: user.id,
      username: user.username || user.profile?.displayName || 'Anonymous',
      timestamp: new Date().toISOString()
    };

    // Clear draft
    localStorage.removeItem('review-draft');
    
    if (onSubmit) {
      onSubmit(reviewData);
    }
  };

  const QuickRatingButton = ({ value, currentValue, onChange, children, color = '#2196F3' }) => (
    <TouchButton
      onClick={() => onChange(value)}
      variant={currentValue === value ? 'primary' : 'outline'}
      style={{
        flex: 1,
        margin: '4px',
        backgroundColor: currentValue === value ? color : 'transparent',
        borderColor: color,
        color: currentValue === value ? 'white' : color,
        minHeight: '60px',
        fontSize: '14px',
        fontWeight: '600'
      }}
    >
      {children}
    </TouchButton>
  );

  const StarRating = ({ rating, onRatingChange, label }) => (
    <TouchFormGroup>
      <label style={{ 
        display: 'block', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333',
        marginBottom: '12px'
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <TouchButton
            key={star}
            onClick={() => onRatingChange(star)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '32px',
              color: rating >= star ? '#FFD700' : '#ddd',
              padding: '8px',
              minWidth: '48px',
              minHeight: '48px',
              borderRadius: '50%'
            }}
          >
            ★
          </TouchButton>
        ))}
      </div>
      <div style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
        {rating > 0 ? `${rating}/5` : 'No rating'}
      </div>
    </TouchFormGroup>
  );

  const renderLocationStep = () => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', textAlign: 'center', marginBottom: '24px' }}>
        📍 Location Details
      </h2>
      
      <TouchFormGroup>
        <TouchInput
          label="Location Name"
          value={formData.locationName}
          onChange={(e) => updateFormData('locationName', e.target.value)}
          placeholder="Enter location name"
          required
        />
      </TouchFormGroup>

      <TouchFormGroup>
        <TouchButton
          onClick={getCurrentLocation}
          disabled={isLoadingLocation}
          variant="outline"
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          {isLoadingLocation ? '🔄 Getting Location...' : '📍 Use Current Location'}
        </TouchButton>
        {formData.coordinates && (
          <div style={{ 
            marginTop: '8px', 
            fontSize: '12px', 
            color: '#666',
            textAlign: 'center'
          }}>
            Location detected: {formData.coordinates.lat.toFixed(4)}, {formData.coordinates.lng.toFixed(4)}
          </div>
        )}
      </TouchFormGroup>
    </div>
  );

  const renderRatingStep = () => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', textAlign: 'center', marginBottom: '24px' }}>
        ⭐ Quick Ratings
      </h2>

      <StarRating
        rating={formData.overallSafety}
        onRatingChange={(value) => updateFormData('overallSafety', value)}
        label="Overall Safety Feeling *"
      />

      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '16px', 
          fontWeight: '600',
          color: '#333',
          marginBottom: '12px'
        }}>
          Lighting Quality
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <QuickRatingButton
            value={1}
            currentValue={formData.lightingQuality}
            onChange={(value) => updateFormData('lightingQuality', value)}
            color="#f44336"
          >
            Poor
          </QuickRatingButton>
          <QuickRatingButton
            value={2}
            currentValue={formData.lightingQuality}
            onChange={(value) => updateFormData('lightingQuality', value)}
            color="#ff9800"
          >
            Fair
          </QuickRatingButton>
          <QuickRatingButton
            value={3}
            currentValue={formData.lightingQuality}
            onChange={(value) => updateFormData('lightingQuality', value)}
            color="#2196f3"
          >
            Good
          </QuickRatingButton>
          <QuickRatingButton
            value={4}
            currentValue={formData.lightingQuality}
            onChange={(value) => updateFormData('lightingQuality', value)}
            color="#4caf50"
          >
            Very Good
          </QuickRatingButton>
          <QuickRatingButton
            value={5}
            currentValue={formData.lightingQuality}
            onChange={(value) => updateFormData('lightingQuality', value)}
            color="#4caf50"
          >
            Excellent
          </QuickRatingButton>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '16px', 
          fontWeight: '600',
          color: '#333',
          marginBottom: '12px'
        }}>
          Crowd Level
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <QuickRatingButton
            value={1}
            currentValue={formData.crowdLevels}
            onChange={(value) => updateFormData('crowdLevels', value)}
          >
            Empty
          </QuickRatingButton>
          <QuickRatingButton
            value={2}
            currentValue={formData.crowdLevels}
            onChange={(value) => updateFormData('crowdLevels', value)}
          >
            Light
          </QuickRatingButton>
          <QuickRatingButton
            value={3}
            currentValue={formData.crowdLevels}
            onChange={(value) => updateFormData('crowdLevels', value)}
          >
            Moderate
          </QuickRatingButton>
          <QuickRatingButton
            value={4}
            currentValue={formData.crowdLevels}
            onChange={(value) => updateFormData('crowdLevels', value)}
          >
            Busy
          </QuickRatingButton>
          <QuickRatingButton
            value={5}
            currentValue={formData.crowdLevels}
            onChange={(value) => updateFormData('crowdLevels', value)}
          >
            Very Crowded
          </QuickRatingButton>
        </div>
      </div>

      <StarRating
        rating={formData.staffHelpfulness}
        onRatingChange={(value) => updateFormData('staffHelpfulness', value)}
        label="Staff Helpfulness"
      />
    </div>
  );

  const renderPhotosStep = () => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', textAlign: 'center', marginBottom: '24px' }}>
        📸 Add Photos
      </h2>

      <TouchFormGroup>
        <TouchButton
          onClick={() => cameraInputRef.current?.click()}
          variant="outline"
          style={{
            width: '100%',
            padding: '20px',
            fontSize: '16px',
            fontWeight: '600',
            borderStyle: 'dashed',
            borderWidth: '2px'
          }}
        >
          📷 Take Photo or Upload from Gallery
        </TouchButton>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handleCameraCapture}
          style={{ display: 'none' }}
        />
      </TouchFormGroup>

      {formData.photos.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: '12px',
          marginTop: '20px'
        }}>
          {formData.photos.map((photo) => (
            <div key={photo.id} style={{ position: 'relative' }}>
              <img
                src={photo.preview}
                alt={photo.name}
                style={{
                  width: '100%',
                  height: '120px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '2px solid #ddd'
                }}
              />
              <TouchButton
                onClick={() => removePhoto(photo.id)}
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  fontSize: '16px',
                  padding: '0',
                  border: 'none'
                }}
              >
                ×
              </TouchButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCommentsStep = () => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', textAlign: 'center', marginBottom: '24px' }}>
        💬 Additional Comments
      </h2>

      <TouchFormGroup>
        <TouchTextArea
          label="Share your experience"
          value={formData.comments}
          onChange={(e) => updateFormData('comments', e.target.value)}
          placeholder="Tell others about your experience at this location..."
          rows={6}
        />
      </TouchFormGroup>

      {recognition && (
        <TouchFormGroup>
          <TouchButton
            onClick={startVoiceRecording}
            disabled={isRecording}
            variant="outline"
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
              backgroundColor: isRecording ? '#f44336' : 'transparent',
              color: isRecording ? 'white' : '#2196F3',
              borderColor: isRecording ? '#f44336' : '#2196F3'
            }}
          >
            {isRecording ? '🔴 Recording...' : '🎤 Voice-to-Text'}
          </TouchButton>
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            textAlign: 'center', 
            marginTop: '8px' 
          }}>
            Tap to speak your comments
          </div>
        </TouchFormGroup>
      )}
    </div>
  );

  const renderReviewStep = () => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '600', textAlign: 'center', marginBottom: '24px' }}>
        ✅ Review & Submit
      </h2>

      <div style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
          📍 {formData.locationName}
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
          <div>Overall Safety: ⭐ {formData.overallSafety}/5</div>
          <div>Lighting: ⭐ {formData.lightingQuality}/5</div>
          <div>Crowd Level: 👥 {formData.crowdLevels}/5</div>
          <div>Staff Help: 🏪 {formData.staffHelpfulness}/5</div>
        </div>

        {formData.photos.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            📸 {formData.photos.length} photo{formData.photos.length !== 1 ? 's' : ''} attached
          </div>
        )}

        {formData.comments && (
          <div style={{ marginTop: '12px' }}>
            <strong>Comments:</strong> {formData.comments.substring(0, 100)}
            {formData.comments.length > 100 ? '...' : ''}
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: '#e3f2fd',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#1976d2'
      }}>
        💾 Your review is automatically saved as a draft and can be retrieved if interrupted.
      </div>
    </div>
  );

  const renderStepContent = () => {
    const stepType = STEP_ORDER[currentStep];
    
    switch (stepType) {
      case WIZARD_STEPS.LOCATION:
        return renderLocationStep();
      case WIZARD_STEPS.RATING:
        return renderRatingStep();
      case WIZARD_STEPS.PHOTOS:
        return renderPhotosStep();
      case WIZARD_STEPS.COMMENTS:
        return renderCommentsStep();
      case WIZARD_STEPS.REVIEW:
        return renderReviewStep();
      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        margin: '20px'
      }}>
        <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>🔐 Login Required</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          Please log in to submit a location review and help keep our community safe.
        </p>
        <TouchButton onClick={() => window.location.reload()}>
          Go to Login
        </TouchButton>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: '#fff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      minHeight: '80vh'
    }}>
      {/* Progress Bar */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '16px 20px',
        borderBottom: '1px solid #e9ecef'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>
            Step {currentStep + 1} of {STEP_ORDER.length}
          </span>
          <span style={{ fontSize: '12px', color: '#666' }}>
            Auto-saved
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '4px',
          backgroundColor: '#e9ecef',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${((currentStep + 1) / STEP_ORDER.length) * 100}%`,
            height: '100%',
            backgroundColor: '#2196F3',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Step Content */}
      <div style={{ minHeight: '400px' }}>
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div style={{
        padding: '20px',
        borderTop: '1px solid #e9ecef',
        display: 'flex',
        gap: '12px'
      }}>
        {currentStep > 0 && (
          <TouchButton
            onClick={prevStep}
            variant="outline"
            style={{ flex: 1 }}
          >
            ← Previous
          </TouchButton>
        )}
        
        {currentStep < STEP_ORDER.length - 1 ? (
          <TouchButton
            onClick={nextStep}
            style={{ flex: 1 }}
          >
            Next →
          </TouchButton>
        ) : (
          <TouchButton
            onClick={handleSubmit}
            style={{ 
              flex: 1,
              backgroundColor: '#4CAF50',
              borderColor: '#4CAF50'
            }}
          >
            📝 Submit Review
          </TouchButton>
        )}
        
        {onCancel && (
          <TouchButton
            onClick={onCancel}
            variant="outline"
            style={{
              flex: currentStep === 0 ? 1 : '0 0 auto',
              color: '#666',
              borderColor: '#ddd'
            }}
          >
            Cancel
          </TouchButton>
        )}
      </div>
    </div>
  );
};

export default ReviewWizard;